import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { platformName } from '../platformMeta';
import type { CalendarSocialPostMissing } from '../types';
import type { PostStateActionsProps } from './dayPanelTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

const socialMissingMessages = defineMessages<CalendarSocialPostMissing>({
  notRead: {
    id: 'app.sml.promote.calendar.published.socialNotRead',
    defaultMessage: 'Its numbers show in Social after the next daily read.',
  },
  beforeHistory: {
    id: 'app.sml.promote.calendar.published.socialBeforeHistory',
    defaultMessage: 'This post is older than the history of your plan, so Social does not show it.',
  },
  otherAccount: {
    id: 'app.sml.promote.calendar.published.socialOtherAccount',
    defaultMessage: 'This post is on an account that the channel no longer uses, so Social does not show it.',
  },
});

/**
 * A published post (design b4): a link to the post and "Open in Social", no edit. "Open in Social"
 * opens the Social detail of this post (`/social/post/:socialPostId`). With no social post to open
 * (the daily read did not find it yet, it is too old for the plan, or another account) the button
 * is hidden and one line says why. A post that the artist posted by hand has no link and says so.
 */
const PublishedActionsComponent: React.FC<PostStateActionsProps> = ({ post, onOpenSocialPost }) => {
  const intl = useIntl();
  const platform = platformName(post.platform);
  const { socialPostId, socialPostMissing } = post;
  const openSocial = useCallback(() => {
    if (socialPostId !== null) {
      onOpenSocialPost(socialPostId);
    }
  }, [onOpenSocialPost, socialPostId]);

  return (
    <>
      {post.externalPostUrl === null ? (
        <div className={styles.dpBody}>
          <span className={styles.tiny}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.published.byHand', defaultMessage: 'Posted by hand' })}
          </span>
        </div>
      ) : null}
      {socialPostId === null && socialPostMissing !== null ? (
        <div className={styles.dpBody}>
          <span className={styles.tiny} data-testid="social-missing">
            {intl.formatMessage(socialMissingMessages[socialPostMissing])}
          </span>
        </div>
      ) : null}
      <div className={styles.dpActs}>
        <div className={styles.row2}>
          {post.externalPostUrl === null ? null : (
            <a className="btn btn-sm btn-outline-primary" href={post.externalPostUrl} target="_blank" rel="noreferrer">
              {intl.formatMessage(
                { id: 'app.sml.promote.post.viewOn', defaultMessage: 'View on {platform}' },
                { platform }
              )}
            </a>
          )}
          {socialPostId === null ? null : (
            <SMLButton size="sm" outline omitCap onClick={openSocial}>
              {intl.formatMessage({
                id: 'app.sml.promote.calendar.published.social',
                defaultMessage: 'Open in Social',
              })}
            </SMLButton>
          )}
        </div>
      </div>
    </>
  );
};

export const PublishedActions = TypedMemo(PublishedActionsComponent);
